import { useEffect } from 'react'
import { AsyncTaskConfig } from 'src/redux/slices/async-processing/consts'
import {
  checkAsyncStatus,
  startAsyncTask
} from 'src/redux/slices/async-processing/endpoints'
import {
  selectBody,
  selectCachedData,
  selectInitiator,
  selectMethod
} from 'src/redux/slices/async-processing/selectors'

import { skipToken } from '@reduxjs/toolkit/query/react'

import { useAppDispatch, useAppSelector } from '../redux/hooks'

const useStartAsyncTask = startAsyncTask.useMutation
const useCheckAsyncStatus = checkAsyncStatus.useQuery

type AsyncTaskAutomatorProps = {
  endpoint: keyof typeof AsyncTaskConfig
}

const AsyncTaskAutomator: React.FC<AsyncTaskAutomatorProps> = ({ endpoint }) => {
  const dispatch = useAppDispatch()

  const initiator = useAppSelector(selectInitiator)
  const body = useAppSelector(selectBody(endpoint))
  const method = useAppSelector(selectMethod(endpoint))
  const cachedData = useAppSelector(selectCachedData(endpoint))

  const [trigger, { data: asyncMetadata, reset }] = useStartAsyncTask({
    fixedCacheKey: endpoint,
  })

  const queryArg =
    asyncMetadata?.taskLocation && !cachedData
      ? {
          url: asyncMetadata.taskLocation,
          endpoint,
        }
      : skipToken

  const { isError: isAsyncError } = useCheckAsyncStatus(queryArg, { pollingInterval: 1000 })

  useEffect(() => {
    if (isAsyncError) {
      reset()
    }
  }, [reset, isAsyncError])

  useEffect(() => {
    if (initiator === endpoint) {
      trigger({ body, method, endpoint })
    }
  }, [body, method, endpoint, initiator, dispatch, trigger])

  return <></>
}

export default AsyncTaskAutomator
